import { useEffect, useState } from 'react';

import { useFolderAction } from '@_features/folder';
import { FOLDER_CONSTANTS } from '@_entities/content';

import { SpinnerIcon } from '@_shared/icons';
import { useUILayerStore } from '@_shared/stores/useUILayerStore';
import { InputField } from '@_shared/ui/InputField';
import { SingleInputFormPopup } from '@_shared/ui/Popup';
import { Typography } from '@_shared/ui/Typography';

import { sanitizeFolderName } from '../../libs';
import * as S from './FolderNameUpdatePopup.style';

interface IProps {
	folderId: string;
	initialFolderName: string;
}

export function FolderNameUpdatePopup({ folderId, initialFolderName }: IProps) {
	const { popLayer } = useUILayerStore();
	const {
		updateFolderName,
		mutateProps: { isError, isPending, isSuccess },
	} = useFolderAction();
	const [folderNameInput, setFolderNameInput] = useState<string>(initialFolderName);
	const [isUpdateError, setIsUpdateError] = useState<boolean>(isError);

	function handleOnInput(e: React.ChangeEvent<HTMLInputElement>) {
		const sanitizedFolderName = sanitizeFolderName(e.target.value);

		e.target.value = sanitizedFolderName;
		setFolderNameInput(sanitizedFolderName);
		setIsUpdateError(false);
	}

	function handleSubmit() {
		const trimmedFolderNameInput = folderNameInput.trim();

		updateFolderName({ folderId, name: trimmedFolderNameInput });
	}

	function isSubmitDisabled() {
		return folderNameInput === initialFolderName || folderNameInput.length === 0;
	}

	useEffect(() => {
		if (isPending === false && isError === false && isSuccess === true) {
			popLayer();
			popLayer();
		}
	}, [isPending, isError, isSuccess]);

	useEffect(() => {
		setIsUpdateError(isError);
	}, [isError]);

	return (
		<SingleInputFormPopup.Container closePopup={popLayer}>
			<SingleInputFormPopup.TextContainer>
				<SingleInputFormPopup.Title>폴더 이름 변경</SingleInputFormPopup.Title>
				<SingleInputFormPopup.Button
					disabled={isSubmitDisabled() || isUpdateError}
					onClick={() => {
						handleSubmit();
					}}
				>
					<Typography
						type="title-small"
						color={
							isSubmitDisabled() || isUpdateError ? 'font-tertiary' : 'orange-primary'
						}
					>
						저장
					</Typography>
				</SingleInputFormPopup.Button>
			</SingleInputFormPopup.TextContainer>

			<InputField
				value={folderNameInput}
				onChange={handleOnInput}
				type="text"
				minLength={FOLDER_CONSTANTS.FOLDER_NAME_MIN_LENGTH}
				maxLength={FOLDER_CONSTANTS.FOLDER_NAME_MAX_LENGTH}
				onInput={handleOnInput}
				defaultValue={initialFolderName}
				placeholder="폴더 이름을 입력해주세요."
				borderColor={isUpdateError ? 'orange-primary' : 'warmgray-200'}
			>
				<S.Counter type="label-medium" color="warmgray-400">
					{folderNameInput.length}/{FOLDER_CONSTANTS.FOLDER_NAME_MAX_LENGTH}
				</S.Counter>
			</InputField>

			{/* 이미 사용 중인 폴더명일 경우, 에러 메시지 표시 */}
			{/* {isError && ( */}
			{isUpdateError && (
				<SingleInputFormPopup.ErrorMessage>
					이미 사용 중인 폴더명입니다.
				</SingleInputFormPopup.ErrorMessage>
			)}

			{/* 폴더 이름 변경 요청 간, 로딩 스피너 */}
			{isPending && (
				<S.LoadingWrapper>
					<SpinnerIcon />
				</S.LoadingWrapper>
			)}
		</SingleInputFormPopup.Container>
	);
}
